// Fiche d'une démarche : état et motif, versions (éditeur, aperçu, comparaison), nouvelle
// version, suspension, réactivation, retrait, statistiques des dossiers, migration par lot.
import type { Demarche } from '@cc/contrats';
import { Bouton, Lien, LienBouton, Panneau, PuceEtat, Tableau, type ColonneTableau } from '@cc/ui';
import { ArrowLeft, Plus } from 'lucide-react';
import { useNavigate, useParams } from 'react-router';
import { requete } from '../../api/client';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { ActionMetier } from '../metier/ActionMetier';
import { LIBELLES_DOMAINES_DEMARCHE, LIBELLES_ETAT_DEMARCHE, LIBELLES_ETAT_VERSION, useDemarche } from './contenu';
import { MigrationLot } from './MigrationLot';
import { compterParDemarche, puceDemarche, useStatistiquesDossiers } from './PageDemarches';
import commun from '../../commun/commun.module.css';
import styles from './demarches.module.css';

type Version = Demarche['versions'][number];

function Statistiques({ demarche }: { demarche: Demarche }) {
  const stats = useStatistiquesDossiers();
  const c = compterParDemarche(stats.data?.elements).get(demarche.id);
  const parEtat = new Map<string, number>();
  for (const d of stats.data?.elements ?? []) if (d.demarche.id === demarche.id) parEtat.set(d.statut_usager_libelle, (parEtat.get(d.statut_usager_libelle) ?? 0) + 1);
  return (
    <Panneau titre="Statistiques">
      {stats.isPending ? <p className={commun.na}>Calcul en cours.</p> : null}
      {stats.error ? <p className={commun.na}>Les dossiers ne sont pas lisibles avec vos droits : aucune statistique.</p> : null}
      {stats.data ? (
        <dl className={commun.definitions}>
          <dt>Versions</dt>
          <dd>{demarche.versions.length}</dd>
          <dt>Dossiers</dt>
          <dd>{c?.total ?? 0} dont {c?.en_cours ?? 0} en cours</dd>
          {[...parEtat.entries()].map(([statut, n]) => (
            <div key={statut} style={{ display: 'contents' }}>
              <dt>{statut}</dt>
              <dd>{n}</dd>
            </div>
          ))}
          <dt>Base de calcul</dt>
          <dd className={commun.na}>
            {stats.data.elements.length} dossiers les plus récents lisibles{stats.data.suivant ? ', d\'autres existent' : ''}
          </dd>
        </dl>
      ) : null}
    </Panneau>
  );
}

export function PageDemarche() {
  const { id = '' } = useParams();
  const { contexte } = useSession();
  const navigate = useNavigate();
  const demarche = useDemarche(id);
  const d = demarche.data;
  const invalider = [['demarches'], ['indicateurs']];
  const nouvelle = useAction({
    executer: () => requete<{ numero: number }>(`/demarches/${id}/versions`, { methode: 'POST', corps: {} }),
    succes: (v) => `Version ${v.numero} créée en brouillon à partir de la version publiée.`,
    invalider,
    apres: (v) => navigate(`/demarches/${id}/versions/${v.numero}`),
  });
  const peutCreer = peut(contexte, 'demarche_config', 'creer') && !d?.interne;
  const peutValider = peut(contexte, 'demarche_config', 'valider') && !d?.interne;

  const colonnes: ColonneTableau<Version>[] = [
    { id: 'numero', entete: 'Version', enTeteLigne: true, masquable: false, numerique: true, valeurTri: (v) => v.numero, cellule: (v) => <Lien href={`/demarches/${id}/versions/${v.numero}`}>Version {v.numero}</Lien> },
    { id: 'etat', entete: 'État', cellule: (v) => <PuceEtat ton={v.etat === 'publiee' ? 'ok' : v.etat === 'en_verification' ? 'attente' : 'neutre'} libelle={LIBELLES_ETAT_VERSION[v.etat] ?? v.etat} />, valeurTri: (v) => v.etat },
    { id: 'publiee', entete: 'Publiée le', cellule: (v) => (v.publiee_le ? date(v.publiee_le) : 'non publiée'), valeurTri: (v) => v.publiee_le },
    {
      id: 'actions',
      entete: 'Actions',
      cellule: (v) => (
        <span className={commun.ligne}>
          <Lien href={`/demarches/${id}/versions/${v.numero}`}>{v.etat === 'brouillon' ? 'Ouvrir l\'éditeur' : 'Consulter'}</Lien>
          <Lien href={`/demarches/${id}/versions/${v.numero}/apercu`}>Aperçu</Lien>
          {v.numero > 1 ? <Lien href={`/demarches/${id}/comparaison?de=${v.numero - 1}&a=${v.numero}`}>Comparer avec la version {v.numero - 1}</Lien> : null}
        </span>
      ),
    },
  ];

  return (
    <>
      <EnTetePage
        etiquette={d ? `Démarches / ${LIBELLES_DOMAINES_DEMARCHE[d.domaine] ?? d.domaine}` : 'Démarches'}
        titre={d?.libelle ?? 'Démarche'}
        description={
          d ? (
            <div className={styles.statut}>
              {puceDemarche(d.etat)}
              <span className="cc-mono">{d.code}</span>
              {d.version_courante ? <span>Version {d.version_courante.numero} publiée le {date(d.version_courante.publiee_le)}</span> : <span>Aucune version publiée</span>}
              {d.version_en_cours ? <span>Version {d.version_en_cours.numero} en préparation ({LIBELLES_ETAT_VERSION[d.version_en_cours.etat]})</span> : null}
              {d.interne ? <span>Démarche interne de test</span> : null}
            </div>
          ) : null
        }
        actions={
          <>
            <LienBouton href="/demarches" icone={ArrowLeft}>
              Démarches
            </LienBouton>
            {d && peutCreer && d.version_courante && !d.version_en_cours && d.etat !== 'retiree' ? (
              <Bouton variante="principal" icone={Plus} chargement={nouvelle.enCours} onClick={() => nouvelle.lancer(undefined)}>
                Nouvelle version
              </Bouton>
            ) : null}
            {d && d.version_en_cours ? (
              <LienBouton href={`/demarches/${id}/versions/${d.version_en_cours.numero}`} variante="principal">
                {d.version_en_cours.etat === 'brouillon' ? 'Poursuivre la version ' : 'Vérifier la version '}
                {d.version_en_cours.numero}
              </LienBouton>
            ) : null}
          </>
        }
      />
      <EtatRequete chargement={demarche.isPending} erreur={demarche.error} onReessayer={() => void demarche.refetch()} lot="L1-A" fonction="La fiche de la démarche">
        {d ? (
          <div className={commun.deuxColonnes}>
            <div className={commun.pile}>
              {d.motif_etat && (d.etat === 'suspendue' || d.etat === 'retiree') ? (
                <Panneau titre={d.etat === 'suspendue' ? 'Démarche suspendue' : 'Démarche retirée'}>
                  <p>Motif : {d.motif_etat}</p>
                  <p className={commun.na}>{d.etat === 'suspendue' ? 'Aucun nouveau dépôt n\'est accepté ; les dossiers en cours continuent.' : 'La démarche n\'accepte plus de dépôt et ne peut plus être publiée.'}</p>
                </Panneau>
              ) : null}
              <Panneau titre="Versions">
                {nouvelle.messageErreur ? <p className={commun.na}>{nouvelle.messageErreur}</p> : null}
                <Tableau legende="Versions de la démarche" colonnes={colonnes} lignes={[...d.versions].sort((a, b) => b.numero - a.numero)} cleLigne={(v) => v.id} />
              </Panneau>
              {peutValider ? <MigrationLot demarche={d} /> : null}
            </div>
            <div className={commun.pile}>
              <Panneau titre="Cycle de vie">
                <p>
                  {LIBELLES_ETAT_DEMARCHE[d.etat] ?? d.etat}. {d.description || ''}
                </p>
                {peutValider ? (
                  <div className={commun.ligne}>
                    {d.etat === 'publiee' ? <ActionMetier libelle="Suspendre" titre="Suspendre la démarche" explication="Aucun nouveau dépôt ne sera accepté, dans le portail comme au guichet ; les dossiers en cours continuent. La démarche pourra être réactivée." chemin={`/demarches/${id}/suspendre`} champs={[{ nom: 'motif', libelle: 'Motif de la suspension', obligatoire: true, multiligne: true }]} succes="Démarche suspendue." invalider={invalider} variante="secondaire" /> : null}
                    {d.etat === 'suspendue' ? <ActionMetier libelle="Réactiver" titre="Réactiver la démarche" explication="La démarche accepte de nouveau les dépôts avec sa version publiée." chemin={`/demarches/${id}/reactiver`} champs={[{ nom: 'motif', libelle: 'Motif de la réactivation', obligatoire: true, multiligne: true }]} succes="Démarche réactivée." invalider={invalider} variante="principal" /> : null}
                    {d.etat !== 'retiree' ? <ActionMetier libelle="Retirer" titre="Retirer la démarche" explication="Retrait définitif : plus aucun dépôt ni publication. Les dossiers existants restent consultables et poursuivent leur traitement." chemin={`/demarches/${id}/retirer`} champs={[{ nom: 'motif', libelle: 'Motif du retrait', obligatoire: true, multiligne: true }]} succes="Démarche retirée." invalider={invalider} variante="danger" /> : null}
                  </div>
                ) : (
                  <p className={commun.na}>La suspension, la réactivation et le retrait sont réservés au droit de valider la configuration.</p>
                )}
              </Panneau>
              <Statistiques demarche={d} />
            </div>
          </div>
        ) : null}
      </EtatRequete>
    </>
  );
}
